import {
  Box,
  TableContainer,
  Typography,
  Card,
  CardContent,
  Stack,
} from "@mui/material";
import React from "react";
import {
  FormattedManagementsList,
  ManagementsDetailHeader,
} from "../../../core/models/managements/managements.model";
import "./ServicesHistoric.scss";
import { useTranslation } from "react-i18next";
import { formatDate } from "../../../utils/dateUtils/formatDate.util";
import { getManagementStateData } from "../../../utils/components/managements/managements.util";
import theme from "../../../muiTheme";
import ManagementsPagination from "../ManagementsPagination/ManagementsPagination";
import LoadingComponentComponent from "../../UI/LoadingComponentComponent/LoadingComponentComponent";
import { useNavigate } from "react-router-dom";
import { getServicesCodeTextIcon } from "../../../utils/components/services/services.util";

interface ManagementsHistoricProps {
  managementsHistoricList: FormattedManagementsList[];
  totalRegistersQuery: number;
  isHistoricTableLoading: boolean;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  rowsPerPage: number;
  setRowsPerPage: React.Dispatch<React.SetStateAction<number>>;
  setManagementsDetailHeader: React.Dispatch<
    React.SetStateAction<ManagementsDetailHeader>
  >;
}

const ServicesHistoric = ({
  managementsHistoricList,
  totalRegistersQuery,
  isHistoricTableLoading,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  setManagementsDetailHeader,
}: ManagementsHistoricProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Box
      component="section"
      sx={{
        paddingTop: { xs: "2.5rem", md: "5rem" },
        paddingBottom: { xs: "3.5rem", md: "5rem" },
        cursor: "pointer",
      }}
    >
      <Typography component="h3" variant="h3" paddingBottom="2.5rem">
        {t("Servicios.Servicios en curso")}
      </Typography>
      {!managementsHistoricList.length && !isHistoricTableLoading && (
        <Typography
          component="p"
          variant="body2"
          sx={{ color: "grey.700" }}
          paddingBottom={{ xs: "1.5rem", md: "2rem" }}
        >
          {t("Servicios.No hay histórico de servicios")}
        </Typography>
      )}
      {isHistoricTableLoading && (
        <Box padding={"2rem 0"}>
          <LoadingComponentComponent
            isLoading={isHistoricTableLoading}
            backgroundColor={"transparent"}
            position={"relative"}
            height={"4rem"}
          />
        </Box>
      )}
      <Box component="article" className="last-services-table">
        <TableContainer sx={{ overflow: "visible" }}>
          {managementsHistoricList.map(
            ({
              issueNumber,
              date,
              state,
              recordTextType,
              claimTextSubType,
              incidenceText,
            }) => {
              const { color, backgroundColor, icon, label } =
                getManagementStateData(state);

              const { icon: iconService, text } =
                getServicesCodeTextIcon(claimTextSubType);
              return (
                <Card
                  className="last-services-table__card"
                  sx={{ borderRadius: "12px", marginBottom: "1rem" }}
                  key={issueNumber}
                  data-testid={issueNumber}
                  onClick={() => {
                    navigate(
                      `${process.env.REACT_APP_SERVICES_DETAIL}/${issueNumber}`
                    );
                    setManagementsDetailHeader({
                      recordTextType,
                      claimTextSubType,
                      incidenceText,
                    });
                  }}
                >
                  <CardContent sx={{ "&&": { padding: "16px" } }}>
                    <Stack justifyContent="space-between" direction="row">
                      <Stack direction="row" gap="2rem">
                        <Typography
                          component="p"
                          fontSize={"32px"}
                          color="primary.main"
                          margin={{ xs: "0", md: "auto 0" }}
                          width={"2rem"}
                        >
                          <span className={iconService} />
                        </Typography>
                        <Stack
                          direction={{ xs: "column", md: "row" }}
                          gap={{ xs: "0.5rem", md: "2rem" }}
                          justifyContent={"space-between"}
                          width={{ xs: "100%", md: "30rem" }}
                        >
                          <Stack direction="column">
                            <Typography component="h6" variant="body2">
                              {t(
                                !text
                                  ? "Gestiones.Otras gestiones"
                                  : `Servicios.${text}`
                              )}
                            </Typography>
                            <Typography variant="caption">
                              {`${t("Gestiones.Solicitado el")} ${formatDate(
                                new Date(date)
                              )}`}
                            </Typography>
                          </Stack>
                          <Typography
                            variant="body2"
                            component="span"
                            width={"fit-content"}
                            sx={{
                              backgroundColor,
                              color,
                            }}
                            className="state-chip"
                            height={"fit-content"}
                            alignSelf={{ xs: "start", md: "center" }}
                          >
                            <span aria-hidden="true" className={icon} />
                            {t(label)}
                          </Typography>
                        </Stack>
                      </Stack>
                      <Typography
                        component="h5"
                        variant="h5"
                        color={theme.palette.primary.main}
                        margin="auto 0"
                      >
                        <span
                          className="t-icon-long-arrow-right"
                          style={{ alignSelf: "flex-end" }}
                        />
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>
              );
            }
          )}
          <ManagementsPagination
            page={page}
            setPage={setPage}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            totalRegistersQuery={totalRegistersQuery}
          />
        </TableContainer>
      </Box>
    </Box>
  );
};

export default ServicesHistoric;
